/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { useEffect, useMemo, useState, FC, useRef } from "react";
import { t } from "@bookingcom/lingojs-core";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { Actionable, Text, Popover, SheetContainer, Icon, Button, Title } from "@bookingcom/bui-react";

// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../../store";
import { UIFlightData } from "@flights/types";
import { QuestionMarkCircleIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import useFormatPrice from "hooks/useFormatPrice";
import useUserAgent from "../../../../hooks/useUserAgent";
import usePriceBreakDownForPreOptedAncillaries from "hooks/usePriceBreakDownForPreOptedAncillaries";
import useLocaleContext from "../../../../hooks/useLocaleContext";
import useGlobalContext from "hooks/useGlobalContext";
import getLowestAdultTravellerPrice from "utils/lowest-adult-traveller-price";
import FlightAvailableSeats from "./FlightAvailableSeats";
import useEventTracking from "hooks/useEventTracking";
import useRouteName from "hooks/useRouteName";
import Frame from "components/elements/Frame";
import styles from "./FlightCardPrice.module.css";
import { mcn } from "utils/mergeClassnames";
import { getCfarTicketFullPrice, getFlexibleTicketFullPrice } from "../../CheckoutTicketTypeInner/ticketTypeUtils";
import { getExtOriginMarketingTrackingVariable } from "utils/marketing-url-params";
import { pricingUtils, localizationUtils, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { UnifiedPriceBreakdownFlightCardPrice } from "components/elements/UnifiedPriceBreakdown";
import { useUnifiedBreakdownSrTooltip } from "utils/experiments/pricing/flights_price_breakdown_sr_tooltip_web";
import { useParams } from "react-router-dom";
import useGetSearchResultsBound from "hooks/useGetSearchResultsBound";
import { useSelector } from "react-redux";
import { getSearchResultsFlights } from "store/searchResultsBounds/outboundSelectors";
import flights_split_bounds_us, { splitBoundsTrackingStorage } from "utils/experiments/funnel/flights_split_bounds_us";
import useIsInViewport from "hooks/useIsInViewport";
import { UnifiedPriceTag, type UnifiedPriceTagProps } from "components/elements/UnifiedPriceTag";
import { useTrackUnifiedPriceTag } from "utils/experiments/pricing/flights_unified_price_tag";

type Props = {
  flight: UIFlightData;
  breakdownOpen: boolean;
  isRenderedInModal?: boolean;
  shouldAlwaysShowBreakdown?: boolean;
  /* start - flights_split_bounds_us */
  boundsVariant?: "outbound" | "inbound";
  /* end - flights_split_bounds_us */
  alignment?: "end" | "start";
  popoverDirection?: "bottom" | "top";
  popoverCloseAriaLabel: string;
  totalPriceLabel?: string;
  setBreakdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showTotalPriceIncludingAncillaries?: boolean;
  showPricePerPerson?: boolean;
  showAvailableSeats?: boolean;
  priceFrom?: boolean;
  isFlexTicket?: boolean;
  isCfarTicket?: boolean;
  showPricePerTraveller?: boolean;
  totalPriceTagName?: keyof JSX.IntrinsicElements;
};

const resetBodyOverflow = () => {
  document.body.style.overflow = "auto";
};

const FlightCardPrice: FC<Props> = ({
  flight,
  breakdownOpen,
  setBreakdownOpen,
  shouldAlwaysShowBreakdown = false,
  alignment = "end",
  popoverDirection = "bottom",
  totalPriceLabel,
  showPricePerPerson = false,
  showTotalPriceIncludingAncillaries = false,
  showAvailableSeats = false,
  priceFrom = false,
  isRenderedInModal = false,
  isFlexTicket,
  isCfarTicket,
  popoverCloseAriaLabel,
  totalPriceTagName = "div",
  boundsVariant
}) => {
  const { priceDisplayRequirements, priceBreakdown, travellerPrices, perTravellerPriceDifferences } = flight;
  const i18n = useI18n() as I18nChildContext;
  const { isMobile } = useUserAgent();
  const { isRTL } = useLocaleContext();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { adults, children } = store.searchCriteria;
  const { requestId } = useGlobalContext();
  const { formatPrice } = useFormatPrice();
  const routeName = useRouteName();
  const trackV2 = useEventTracking("flight_details", requestId);
  const isOfMetaOrigin = () => getExtOriginMarketingTrackingVariable();
  const isMeta = isOfMetaOrigin();
  const { getAncillaries, getTotalPrice, getPreOptedAncillaries } = usePriceBreakDownForPreOptedAncillaries(
    isMeta ? flight : undefined
  );
  const ancillariesForPriceBreakDown = showTotalPriceIncludingAncillaries ? getPreOptedAncillaries() : getAncillaries();
  const totalPrice = showTotalPriceIncludingAncillaries
    ? getTotalPrice(priceBreakdown, ancillariesForPriceBreakDown)
    : priceBreakdown;
  const [showPopover, setShowPopover] = useState(false);
  const adultTravellerPrice = getLowestAdultTravellerPrice(travellerPrices);
  const adultTravellerPriceDifference = perTravellerPriceDifferences?.find(
    ({ travellerType }) => travellerType === "ADULT"
  );
  const { trackPriceBreakdownSrTooltip, trackPriceBreakdownSrTooltipStages, trackPriceBreakdownSrTooltipGoals } =
    useUnifiedBreakdownSrTooltip(); // flights_price_breakdown_sr_tooltip_web
  const { trackUnifiedPriceTag, trackUnifiedPriceTagStages, trackUnifiedPriceTagGoals } = useTrackUnifiedPriceTag(); // flights_unified_price_tag

  /* start - flights_price_breakdown_sr_tooltip_web */
  const shouldTrackPriceBreakdownSrTooltip = useMemo(
    () => routeName === "searchresults" && trackPriceBreakdownSrTooltip(),
    [routeName, trackPriceBreakdownSrTooltip]
  );
  const withDiscount = !!flight.appliedDiscounts?.length;
  /* end - flights_price_breakdown_sr_tooltip_web */
  const shouldShowBreakdown =
    boundsVariant !== "outbound" &&
    Boolean(
      shouldAlwaysShowBreakdown ||
        priceDisplayRequirements?.includes("AIR_PRICE_BREAKDOWN") ||
        shouldTrackPriceBreakdownSrTooltip
    );

  const roundPrice = !isOfMetaOrigin() && isMobile;

  const handleInteraction = (e: React.SyntheticEvent, status: boolean) => {
    e.stopPropagation();
    setBreakdownOpen(status);
    isMobile && resetBodyOverflow();
  };

  const isStrikedThroughPricesEnabled = flight.priceBreakdown.showPriceStrikethrough;

  const priceBreakDownIcon = isMobile ? (
    <Actionable
      onClick={(e) => {
        if (routeName.includes("flightdetails")) trackV2("click_price_breakdown");
        /* start - flights_price_breakdown_sr_tooltip_web */
        if (routeName.includes("searchresults")) {
          trackPriceBreakdownSrTooltipGoals.tooltipClick();
        }
        /* end - flights_price_breakdown_sr_tooltip_web */
        handleInteraction(e, true);

        trackUnifiedPriceTagGoals.tooltipClick(); // flights_unified_price_tag
      }}
      attributes={{
        "aria-label": i18n.trans(t("flights_a11y_aria_label_brice_breakdown_question_mark")),
        "data-testid": "flight_card_price_open_price_popover"
      }}
      className={styles.breakDownIconWrapper}
    >
      <Icon svg={QuestionMarkCircleIcon} size="small" className={styles.breakDownIcon} />
    </Actionable>
  ) : (
    <Popover
      position={popoverDirection}
      active={showPopover}
      onOpen={() => {
        setShowPopover(true);
        if (routeName.includes("flightdetails")) {
          trackV2("click_price_breakdown");
        }
        /* start - flights_price_breakdown_sr_tooltip_web */
        if (routeName.includes("searchresults")) {
          trackPriceBreakdownSrTooltipGoals.tooltipClick();
        }
        /* end - flights_price_breakdown_sr_tooltip_web */

        trackUnifiedPriceTagGoals.tooltipClick(); // flights_unified_price_tag
      }}
      onClose={() => {
        setShowPopover(false);
      }}
      closeAriaLabel={popoverCloseAriaLabel}
    >
      <Popover.Trigger>
        {(attributes) => (
          <Button
            variant="tertiary-neutral"
            size="medium"
            attributes={{
              "aria-label": i18n.trans(t("flights_a11y_aria_label_brice_breakdown_question_mark")),
              "data-testid": "flight_card_price_open_price_popover",
              ...attributes
            }}
          >
            <Icon svg={QuestionMarkCircleIcon} className={styles.breakDownIcon} />
          </Button>
        )}
      </Popover.Trigger>
      <Popover.Content
        title={
          <Text variant="headline_3" tagName="h2">
            {i18n.trans(t("flights_price_details_title"))}
          </Text>
        }
        attributes={{ style: { width: 360 }, role: "dialog", "aria-modal": "true" }}
      >
        <UnifiedPriceBreakdownFlightCardPrice flight={flight} />
      </Popover.Content>
    </Popover>
  );

  const formattedTotalPrice = useMemo(() => {
    if (isFlexTicket) {
      const flexibleFullPrice = getFlexibleTicketFullPrice(flight);

      return formatPrice(
        roundPrice && flexibleFullPrice.totalRounded ? flexibleFullPrice.totalRounded : flexibleFullPrice.total,
        {
          decimalPlaces: roundPrice ? 0 : undefined
        }
      );
    }
    if (isCfarTicket) {
      const cfarFullPrice = getCfarTicketFullPrice(flight);
      if (cfarFullPrice) {
        return formatPrice(
          roundPrice && cfarFullPrice.totalRounded ? cfarFullPrice.totalRounded : cfarFullPrice.total,
          {
            decimalPlaces: roundPrice ? 0 : undefined
          }
        );
      }
    }

    return formatPrice(roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total, {
      decimalPlaces: roundPrice ? 0 : undefined
    });
  }, [isFlexTicket, isCfarTicket, formatPrice, flight, roundPrice, totalPrice.totalRounded, totalPrice.total]);

  const selectedOutboundPrice = useGetSelectedOutboundPrice({ isRoundedPrice: roundPrice });

  /* start - flights_split_bounds_us */
  const isNegativeRef = useRef(false);
  const priceElementRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef(false);
  const negativePriceIsVisible = useIsInViewport(priceElementRef.current);

  if (!trackedRef.current && isNegativeRef.current && negativePriceIsVisible) {
    flights_split_bounds_us.goals.negative_incremental_price_visible();
    trackedRef.current = true;
  }
  /* end - flights_split_bounds_us */

  const formattedPricePerTravellerString = useMemo(() => {
    if (
      boundsVariant === "inbound" &&
      !!isFeatureRunningClientSide("FLIGHTS_WEB_SPLIT_BOUNDS_PRICE_DIFFERENCE_FROM_SR")
    ) {
      if (!adultTravellerPriceDifference) {
        throw new Error("Split Bounds: Adult perTravellerPriceDifferences was not found");
      }

      const price =
        roundPrice && adultTravellerPriceDifference.totalRounded
          ? adultTravellerPriceDifference.totalRounded
          : adultTravellerPriceDifference.total;

      if (price.units < 0) {
        splitBoundsTrackingStorage.addValue(flight.token, "price");
        isNegativeRef.current = true;
      }

      const formatted = formatPrice(price, { decimalPlaces: roundPrice ? 0 : undefined }).replace("-", "");

      return price.units < 0
        ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formatted } }))
        : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formatted } }));
    }

    if (!adultTravellerPrice) return "";

    if (boundsVariant === "inbound" && !selectedOutboundPrice) return "";

    const price =
      roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
        : adultTravellerPrice.travellerPriceBreakdown.total;

    if (boundsVariant === "inbound") {
      if (!selectedOutboundPrice) return "";

      const finalPrice = pricingUtils.subtractPrice(price, selectedOutboundPrice);

      const formatted = formatPrice(finalPrice, { decimalPlaces: roundPrice ? 0 : undefined }).replace("-", "");

      if (finalPrice.units < 0) {
        splitBoundsTrackingStorage.addValue(flight.token, "price");
        isNegativeRef.current = true;
      }

      return finalPrice.units < 0
        ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formatted } }))
        : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formatted } }));
    }

    return formatPrice(price, { decimalPlaces: roundPrice ? 0 : undefined });
  }, [
    adultTravellerPrice,
    adultTravellerPriceDifference,
    boundsVariant,
    flight.token,
    formatPrice,
    i18n,
    roundPrice,
    selectedOutboundPrice
  ]);

  const pricePerPersonExplanation = useMemo(() => {
    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      return i18n.trans(
        t("flights_ryanair_hungary_sr_price", {
          variables: {
            price: formattedTotalPrice
          }
        })
      );
    } else if (!!boundsVariant) {
      return i18n.trans(
        t("flights_sr_split_bounds_us_total", {
          variables: { price: formattedTotalPrice }
        })
      );
    } else {
      return i18n.trans(
        t("flights_sr_total_inc_price", {
          variables: { total_price: formattedTotalPrice }
        })
      );
    }
  }, [flight.segments, boundsVariant, i18n, formattedTotalPrice]);

  const priceExplanation = useMemo(() => {
    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      return i18n.trans(t("flights_ryanair_hungary_sr_price_single"));
    } else {
      return totalPriceLabel || i18n.trans(t("flights_price_qualifier_total_for_all"));
    }
  }, [flight.segments, i18n, totalPriceLabel]);

  const strikedThroughPrice = useMemo(
    () =>
      isStrikedThroughPricesEnabled && roundPrice && priceBreakdown.totalWithoutDiscountRounded
        ? formatPrice(priceBreakdown.totalWithoutDiscountRounded, { decimalPlaces: 0 })
        : isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount
        ? formatPrice(priceBreakdown.totalWithoutDiscount)
        : null,
    [
      formatPrice,
      isStrikedThroughPricesEnabled,
      roundPrice,
      priceBreakdown.totalWithoutDiscount,
      priceBreakdown.totalWithoutDiscountRounded
    ]
  );

  const priceAriaLabel = useMemo(() => {
    if (showPricePerPerson && !!adultTravellerPrice) {
      const priceLabel = priceFrom
        ? i18n.trans(
            t("flights_sr_rt_from_price", {
              variables: {
                price: formattedPricePerTravellerString
              }
            })
          )
        : formattedPricePerTravellerString;

      if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
        return `${priceLabel} ${i18n.trans(t("flights_ryanair_hungary_sr_price_single"))}`;
      }

      if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
        return i18n.trans(
          t("a11y_flights_deal_price_new", { variables: { new_price: formattedPricePerTravellerString } })
        );
      }

      return priceLabel;
    }

    const priceLabel = priceFrom
      ? i18n.trans(
          t("flights_sr_rt_from_price", {
            variables: { price: formattedTotalPrice }
          })
        )
      : formattedTotalPrice;

    const travellersCount = adults + children.length;

    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      return `${priceLabel} ${t("flights_ryanair_hungary_bp_price_per", {
        variables: { total_price: priceLabel, num_travellers: travellersCount }
      })}`;
    }

    if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
      return [
        i18n.trans(
          t("a11y_flights_deal_price_old_solo", {
            variables: {
              old_price: strikedThroughPrice
            }
          })
        ),
        i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: formattedTotalPrice } }))
      ].join(", ");
    }

    return priceLabel;
  }, [
    adultTravellerPrice,
    adults,
    children.length,
    flight.segments,
    formattedPricePerTravellerString,
    formattedTotalPrice,
    i18n,
    isStrikedThroughPricesEnabled,
    priceFrom,
    showPricePerPerson,
    strikedThroughPrice
  ]);

  const totalPriceDisplay = useMemo(() => {
    if (showPricePerPerson && !!adultTravellerPrice) {
      return priceFrom
        ? i18n.trans(
            t("flights_sr_rt_from_price", {
              variables: {
                price: formattedPricePerTravellerString
              }
            })
          )
        : formattedPricePerTravellerString;
    }

    const shouldShowStrikedThroughPriceOnTop =
      priceBreakdown.showPriceStrikethrough && (isMobile || !isRenderedInModal);

    return (
      <div className={mcn(!shouldShowStrikedThroughPriceOnTop && styles.priceContainer)}>
        {strikedThroughPrice && shouldShowStrikedThroughPriceOnTop && (
          <Text
            variant="body_2"
            decoration="line-through"
            color="destructive"
            className={mcn(styles.strikedPrice, styles.strikedPriceOnTop)}
          >
            {strikedThroughPrice}
          </Text>
        )}

        {priceFrom
          ? i18n.trans(
              t("flights_sr_rt_from_price", {
                variables: { price: formattedTotalPrice }
              })
            )
          : formattedTotalPrice}

        {strikedThroughPrice && !shouldShowStrikedThroughPriceOnTop && (
          <Text
            variant="small_1"
            decoration="line-through"
            color="destructive"
            className={mcn(styles.strikedPrice, styles.strikedPriceFD)}
          >
            {strikedThroughPrice}
          </Text>
        )}
      </div>
    );
  }, [
    showPricePerPerson,
    adultTravellerPrice,
    priceBreakdown.showPriceStrikethrough,
    isMobile,
    strikedThroughPrice,
    priceFrom,
    i18n,
    isRenderedInModal,
    formattedTotalPrice,
    formattedPricePerTravellerString
  ]);

  const priceExplanationAriaLabel = useMemo(() => {
    const travellersCount = adults + children.length;

    let priceExplanation = i18n.trans(t("flights_price_qualifier_total_for_all"));

    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      priceExplanation = i18n.trans(
        t("flights_ryanair_hungary_bp_price_per", {
          variables: { total_price: formattedTotalPrice, num_travellers: travellersCount }
        })
      );
    } else if (isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount) {
      return i18n.trans(
        t("a11y_flights_deal_price_total", {
          variables: {
            total_price: formattedTotalPrice,
            old_price: formatPrice(priceBreakdown.totalWithoutDiscount)
          }
        })
      );
    }

    return `${formattedTotalPrice} ${priceExplanation}`;
  }, [
    adults,
    children.length,
    flight.segments,
    formatPrice,
    formattedTotalPrice,
    i18n,
    isStrikedThroughPricesEnabled,
    priceBreakdown.totalWithoutDiscount
  ]);

  const ariaExplanationAttributes = useMemo(() => {
    const travellersCount = adults + children.length;
    const attributes = {};

    if (travellersCount === 1) {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      attributes["aria-hidden"] = true;
    } else {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      attributes["aria-label"] = priceExplanationAriaLabel;
    }

    return attributes;
  }, [adults, children.length, priceExplanationAriaLabel]);

  const strikedThroughPricesShouldStack = isMobile && pricingUtils.isPriceWithLargeNumber(priceBreakdown.total);

  const shouldHidePriceExplanation = useMemo<boolean>(() => {
    const isSolo = [...travellerPrices].length === 1;
    if (!["searchresults", "flightdetails"].includes(routeName) || !isSolo) {
      return false;
    }
    return true;
  }, [travellerPrices, routeName]);

  /* start - flights_price_breakdown_sr_tooltip_web */
  useEffect(() => {
    routeName === "searchresults" && trackPriceBreakdownSrTooltipStages({ withDiscount });
  }, [routeName, withDiscount, trackPriceBreakdownSrTooltipStages]);
  /* end - flights_price_breakdown_sr_tooltip_web */

  const travellersCount = adults + children.length;

  // flights_unified_price_tag -- start
  // ==========================================================================
  const shouldShowUnifiedPriceTag = useMemo(() => trackUnifiedPriceTag(), [trackUnifiedPriceTag]);

  const priceTagProps = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag) return;

    const ticketprice =
      (isFlexTicket
        ? getFlexibleTicketFullPrice(flight)
        : isCfarTicket
        ? getCfarTicketFullPrice(flight)
        : totalPrice) || totalPrice;

    const priceTranslations = priceFrom
      ? [
          (formattedPrice: string) =>
            i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
        ]
      : [];

    const strikethroughPrice = priceBreakdown.showPriceStrikethrough
      ? roundPrice && priceBreakdown.totalWithoutDiscountRounded
        ? priceBreakdown.totalWithoutDiscountRounded
        : priceBreakdown.totalWithoutDiscount
      : undefined;
    const strikethroughPricePosition = !!strikethroughPrice && (isMobile || !isRenderedInModal) ? "top" : "end";

    return {
      price: roundPrice && ticketprice.totalRounded ? ticketprice.totalRounded : ticketprice.total,
      priceTranslations,
      strikethroughPrice,
      strikethroughPricePosition,
      shouldRoundPrice: roundPrice
    };
  }, [
    flight,
    i18n,
    isCfarTicket,
    isFlexTicket,
    isMobile,
    isRenderedInModal,
    priceBreakdown,
    priceFrom,
    roundPrice,
    totalPrice,
    shouldShowUnifiedPriceTag
  ]);

  const priceTagPropsWithPricePerAdult = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag || !priceTagProps) return;

    if (
      boundsVariant === "inbound" &&
      !!isFeatureRunningClientSide("FLIGHTS_WEB_SPLIT_BOUNDS_PRICE_DIFFERENCE_FROM_SR")
    ) {
      if (!adultTravellerPriceDifference) {
        throw new Error("Split Bounds: Adult perTravellerPriceDifferences was not found");
      }

      const pricePerAdult =
        roundPrice && adultTravellerPriceDifference.totalRounded
          ? adultTravellerPriceDifference.totalRounded
          : adultTravellerPriceDifference.total;

      if (pricePerAdult.units < 0) {
        splitBoundsTrackingStorage.addValue(flight.token, "price");
        isNegativeRef.current = true;
      }

      const pricePerAdultTranslations = [
        (formattedPrice: string) => formattedPrice.replace("-", ""),
        (formattedPrice: string) =>
          pricePerAdult.units < 0
            ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
            : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
      ];

      if (priceFrom) {
        pricePerAdultTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
        );
      }

      return {
        ...priceTagProps,
        pricePerAdult,
        pricePerAdultTranslations,
        shouldShowPricePerAdult: showPricePerPerson && !!adultTravellerPrice
      };
    }

    if (!adultTravellerPrice) return priceTagProps;

    if (boundsVariant === "inbound" && !selectedOutboundPrice) return priceTagProps;

    const pricePerAdult =
      roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
        : adultTravellerPrice.travellerPriceBreakdown.total;

    if (boundsVariant === "inbound") {
      if (!selectedOutboundPrice) return priceTagProps;

      const finalPricePerAdult = pricingUtils.subtractPrice(pricePerAdult, selectedOutboundPrice);

      if (finalPricePerAdult.units < 0) {
        splitBoundsTrackingStorage.addValue(flight.token, "price");
        isNegativeRef.current = true;
      }

      const pricePerAdultTranslations = [
        (formattedPrice: string) => formattedPrice.replace("-", ""),
        (formattedPrice: string) =>
          finalPricePerAdult.units < 0
            ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
            : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
      ];

      if (priceFrom) {
        pricePerAdultTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
        );
      }

      return {
        ...priceTagProps,
        pricePerAdult: finalPricePerAdult,
        pricePerAdultTranslations,
        shouldShowPricePerAdult: showPricePerPerson && !!adultTravellerPrice
      };
    }

    const pricePerAdultTranslations = priceFrom
      ? [
          (formattedPrice: string) =>
            i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
        ]
      : undefined;

    return {
      ...priceTagProps,
      pricePerAdult,
      pricePerAdultTranslations,
      shouldShowPricePerAdult: showPricePerPerson && !!adultTravellerPrice
    };
  }, [
    adultTravellerPrice,
    adultTravellerPriceDifference,
    boundsVariant,
    flight.token,
    i18n,
    priceFrom,
    priceTagProps,
    roundPrice,
    selectedOutboundPrice,
    showPricePerPerson,
    shouldShowUnifiedPriceTag
  ]);

  const unifiedPriceTagPriceProps = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag || !priceTagPropsWithPricePerAdult) return;

    const pricePropsVariant =
      showPricePerPerson && !!adultTravellerPrice && !priceFrom && isMobile
        ? !!boundsVariant
          ? "headline_3"
          : "strong_1"
        : "headline_3";
    const pricePropsAttributes = {
      "aria-hidden": true,
      "data-testid": "flight_card_price_main_price",
      ref: priceElementRef
    };

    return {
      ...priceTagPropsWithPricePerAdult,
      priceAlignment: alignment,
      priceProps: {
        attributes: pricePropsAttributes,
        tagName: totalPriceTagName,
        variant: pricePropsVariant
      }
    };
  }, [
    adultTravellerPrice,
    alignment,
    boundsVariant,
    isMobile,
    priceFrom,
    priceTagPropsWithPricePerAdult,
    showPricePerPerson,
    totalPriceTagName,
    shouldShowUnifiedPriceTag
  ]);

  const unifiedPriceTagPriceBreakdownProps = useMemo((): Partial<UnifiedPriceTagProps> | undefined => {
    if (!shouldShowUnifiedPriceTag || !shouldShowBreakdown) return;

    return {
      priceBreakdownTrigger: <Icon svg={QuestionMarkCircleIcon} className={styles.uptPriceBreakdownTrigger} />,
      priceBreakdownTriggerProps: {
        attributes: {
          "aria-label": i18n.trans(t("flights_a11y_aria_label_brice_breakdown_question_mark")),
          "data-testid": "flight_card_price_open_price_popover"
        }
      },
      priceBreakdownContent: <UnifiedPriceBreakdownFlightCardPrice flight={flight} />,
      priceBreakdownCloseAriaLabel: popoverCloseAriaLabel,
      priceBreakdownOnOpen: () => {
        if (routeName.includes("flightdetails")) trackV2("click_price_breakdown");

        /* start - flights_price_breakdown_sr_tooltip_web */
        if (routeName.includes("searchresults")) trackPriceBreakdownSrTooltipGoals.tooltipClick();
        /* end - flights_price_breakdown_sr_tooltip_web */

        if (isMobile) {
          resetBodyOverflow();
        }

        trackUnifiedPriceTagGoals.tooltipClick(); // flights_unified_price_tag
      }
    };
  }, [
    flight,
    i18n,
    isMobile,
    popoverCloseAriaLabel,
    routeName,
    shouldShowBreakdown,
    trackPriceBreakdownSrTooltipGoals,
    trackV2,
    shouldShowUnifiedPriceTag,
    trackUnifiedPriceTagGoals
  ]);

  const unifiedPriceTagExplanationProps = useMemo((): Partial<UnifiedPriceTagProps> | undefined => {
    if (!shouldShowUnifiedPriceTag) return;

    if (showPricePerPerson && !!adultTravellerPrice) {
      const pricePerAdultExplanationProps: UnifiedPriceTagProps["pricePerAdultExplanationProps"] = {
        variant: !!boundsVariant ? "small_1" : "body_2",
        attributes: {
          "data-testid": "flight_card_price_total_price",
          ...ariaExplanationAttributes
        }
      };
      const pricePerAdultExplanationTranslations = [];

      if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
        pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_ryanair_hungary_sr_price", { variables: { price: formattedPrice } }))
        );
      } else if (!!boundsVariant) {
        pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_split_bounds_us_total", { variables: { price: formattedPrice } }))
        );
      } else {
        pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_total_inc_price", { variables: { total_price: formattedPrice } }))
        );
      }

      return {
        pricePerAdultExplanationProps,
        pricePerAdultExplanationTranslations
      };
    } else if (!shouldHidePriceExplanation) {
      return {
        priceExplanation,
        priceExplanationProps: { attributes: ariaExplanationAttributes }
      };
    }
    return;
  }, [
    adultTravellerPrice,
    ariaExplanationAttributes,
    boundsVariant,
    flight.segments,
    i18n,
    priceExplanation,
    shouldHidePriceExplanation,
    showPricePerPerson,
    shouldShowUnifiedPriceTag
  ]);

  useEffect(() => {
    trackUnifiedPriceTagStages({
      withPriceBreakdownTooltip: shouldShowBreakdown,
      withPriceStrikethrough:
        priceBreakdown.showPriceStrikethrough &&
        (roundPrice ? !!priceBreakdown.totalWithoutDiscountRounded : !!priceBreakdown.totalWithoutDiscount),
      withPricePerAdultExplanation: showPricePerPerson && !!adultTravellerPrice,
      withPriceExplanation: !(showPricePerPerson && !!adultTravellerPrice) && !shouldHidePriceExplanation,
      withPriceExplanationOnly: false,
      withUsOutboundInbound: false
    });
  }, [
    adultTravellerPrice,
    priceBreakdown,
    roundPrice,
    shouldHidePriceExplanation,
    shouldShowBreakdown,
    showPricePerPerson,
    trackUnifiedPriceTagStages
  ]);
  // ==========================================================================
  // flights_unified_price_tag -- end

  const flightCardPrice = (
    <>
      <div>
        {showAvailableSeats && <FlightAvailableSeats flight={flight} />}
        {!!boundsVariant && (
          <Text variant="small_1" color="neutral_alt">
            {travellersCount > 1
              ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
              : i18n.trans(t("flights_sr_split_bounds_us_round_trip"))}
          </Text>
        )}
        {shouldShowUnifiedPriceTag && unifiedPriceTagPriceProps ? ( // flights_unified_price_tag
          <UnifiedPriceTag
            {...unifiedPriceTagPriceProps}
            {...unifiedPriceTagPriceBreakdownProps}
            {...unifiedPriceTagExplanationProps}
            attributes={{ role: "region", "aria-label": priceAriaLabel }}
          />
        ) : (
          <>
            <Frame
              direction="row"
              justifyContent={alignment === "start" ? "flex-start" : "flex-end"}
              attributes={{ role: "region", "aria-label": priceAriaLabel }}
            >
              <Title
                variant={
                  showPricePerPerson && !!adultTravellerPrice && !priceFrom && isMobile
                    ? !!boundsVariant
                      ? "headline_3"
                      : "strong_1"
                    : "headline_3"
                }
                attributes={{
                  "aria-hidden": true,
                  "data-testid": "flight_card_price_main_price",
                  ref: priceElementRef
                }}
                className={styles.mainPrice}
                titleTagName={totalPriceTagName}
                title={totalPriceDisplay}
              />
              {shouldShowBreakdown && priceBreakDownIcon}
            </Frame>
            <Text
              variant={
                showPricePerPerson && !!adultTravellerPrice && !priceFrom && isMobile
                  ? !!boundsVariant
                    ? "small_1"
                    : "body_2"
                  : "small_1"
              }
              color="neutral_alt"
              attributes={{
                "data-testid": "flight_card_price_total_price",
                ...ariaExplanationAttributes
              }}
            >
              {showPricePerPerson && !!adultTravellerPrice ? (
                <div>
                  {isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount && (
                    <>
                      <Text
                        variant={isMobile ? (!!boundsVariant ? "small_1" : "body_2") : "small_1"}
                        color="destructive"
                        decoration="line-through"
                        className={mcn(
                          styles.strikedPrice,
                          strikedThroughPricesShouldStack ? styles.strikedPriceOnTop : styles.priceInline
                        )}
                      >
                        {strikedThroughPrice}
                      </Text>
                      {/**
                       * To address the bug on RTL price display for multi-travellers
                       * which the currency of the original price is shown within the
                       * read-out of the discounted price
                       *
                       * More info: https://jira.booking.com/jira/browse/FPRICING-807
                       */}
                      {isRTL && (
                        <Text className={styles.priceInline} variant={!!boundsVariant ? "small_1" : "body_2"}>
                          {" "}
                        </Text>
                      )}
                    </>
                  )}
                  <Text className={styles.priceInline} variant={!!boundsVariant ? "small_1" : "body_2"}>
                    {pricePerPersonExplanation}
                  </Text>
                </div>
              ) : (
                <>{!shouldHidePriceExplanation && priceExplanation}</>
              )}
            </Text>
          </>
        )}
      </div>
      {isMobile && breakdownOpen && (
        <SheetContainer
          onCloseTrigger={() => setBreakdownOpen(false)}
          title={i18n.trans(t("flights_price_details_title"))}
          closeAriaLabel={i18n.trans(t("close"))}
          active={true}
          className={styles.drawer}
        >
          <UnifiedPriceBreakdownFlightCardPrice flight={flight} />
        </SheetContainer>
      )}
    </>
  );

  return flightCardPrice;
};

/* start - flights_split_bounds_us */
const useGetSelectedOutboundPrice = ({ isRoundedPrice }: { isRoundedPrice: boolean }) => {
  const bound = useGetSearchResultsBound();
  const params = useParams<{ outboundToken: string }>();
  const outboundFlights = useSelector(getSearchResultsFlights);

  if (bound === "inbound" && params.outboundToken) {
    const selectedFlight = outboundFlights.find((f) => f.outboundFlightInfo?.token === params.outboundToken);

    if (selectedFlight) {
      const adultTravellerPrice = getLowestAdultTravellerPrice(selectedFlight.travellerPrices);

      if (!adultTravellerPrice) return undefined;

      return isRoundedPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
        : adultTravellerPrice.travellerPriceBreakdown.total;
    }
  }

  return undefined;
};
/* end - flights_split_bounds_us */

export default FlightCardPrice;
